import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useI18n } from "@/i18n";
import Seo from "@/components/Seo";
import { routeSlug } from "@/i18n/routes";
import { Kicker, Section, CardGrid, VerticalList } from "@/components/Primitives";
import { Button } from "@/components/ui/button";

const HERO_IMAGE =
  "https://images.pexels.com/photos/12924727/pexels-photo-12924727.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const TEAM_IMAGE =
  "https://images.pexels.com/photos/8117466/pexels-photo-8117466.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const GROWTH_IMAGE =
  "https://images.pexels.com/photos/15361688/pexels-photo-15361688.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Home() {
  const { t, lang } = useI18n();
  const p = (path) => `/${lang}${path}`;
  const pillars = t("home.pillars");

  return (
    <div data-testid="home-page">
      <Seo pageKey="home" />

      <section className="relative overflow-hidden border-b border-border">
        <div className="absolute inset-0">
          <img src={HERO_IMAGE} alt="" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-primary/70" />
        </div>
        <div className="cs-grain relative">
          <div className="cs-container grid grid-cols-1 gap-12 py-28 md:grid-cols-12 md:py-40">
            <div className="md:col-span-8 cs-rise">
              <Kicker className="text-primary-foreground/70">{t("home.heroKicker")}</Kicker>
              <h1 className="mt-8 font-display text-4xl font-light leading-[1.02] text-primary-foreground sm:text-5xl lg:text-6xl">
                <span className="block">{t("home.heroL1")}</span>
                <span className="block">{t("home.heroL2")}</span>
                <span className="block text-accent">{t("home.heroL3")}</span>
              </h1>
              <p className="mt-10 max-w-2xl text-base leading-relaxed text-primary-foreground/85 sm:text-lg">
                {t("home.heroSub")}
              </p>
              <div className="mt-12 flex flex-col gap-4 sm:flex-row">
                <Button asChild size="lg" data-testid="hero-discover-cta" className="rounded-full bg-accent px-8 text-accent-foreground hover:bg-accent/90">
                  <Link to={p(routeSlug("opportunities", lang))}>
                    {t("cta.discover")} <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
                <Button asChild size="lg" variant="outline" data-testid="hero-talk-cta" className="rounded-full border-primary-foreground/40 bg-transparent px-8 text-primary-foreground hover:bg-primary-foreground hover:text-primary">
                  <Link to={p(routeSlug("contact", lang))}>{t("cta.talk")}</Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Section data-testid="pillars-section">
        <Kicker>{t("home.pillarsKicker")}</Kicker>
        <div className="mt-10 grid grid-cols-1 gap-px border border-border bg-border md:grid-cols-3">
          {["projects", "people", "growth"].map((key, index) => (
            <div key={key} data-testid={`pillar-${key}`} className="bg-surface p-8 lg:p-12">
              <span className="cs-kicker">{String(index + 1).padStart(2, "0")}</span>
              <h2 className="mt-6 font-display text-3xl font-medium uppercase tracking-tight">{pillars[key].title}</h2>
              <p className="mt-4 text-sm leading-relaxed text-muted-foreground">{pillars[key].body}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section className="bg-secondary/40">
        <div className="grid grid-cols-1 gap-12 md:grid-cols-12">
          <div className="md:col-span-5">
            <Kicker>{t("home.journeyKicker")}</Kicker>
            <h2 className="mt-6 font-display text-3xl font-light leading-tight sm:text-4xl">{t("home.journeyTitle")}</h2>
            <p className="mt-6 text-sm text-muted-foreground sm:text-base">{t("home.journeyLead")}</p>
            <div className="mt-10 hidden h-56 overflow-hidden border border-border md:block">
              <img src={TEAM_IMAGE} alt="" className="h-full w-full object-cover" loading="lazy" />
            </div>
          </div>
          <div className="md:col-span-7">
            <ol data-testid="journey-list" className="divide-y divide-border border-y border-border">
              {t("home.journey").map((step, index) => (
                <li key={step.title} className="flex gap-6 py-6">
                  <span className="cs-kicker w-8 shrink-0 pt-1">{String(index + 1).padStart(2, "0")}</span>
                  <div>
                    <h3 className="font-display text-xl">{step.title}</h3>
                    <p className="mt-1 text-sm text-muted-foreground">{step.body}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </Section>

      <Section>
        <div className="max-w-3xl">
          <Kicker>{t("home.verticalsKicker")}</Kicker>
          <h2 className="mt-6 font-display text-3xl font-light sm:text-4xl">{t("home.verticalsTitle")}</h2>
          <p className="mt-6 text-sm text-muted-foreground sm:text-base">{t("home.verticalsLead")}</p>
        </div>
        <VerticalList testId="home-verticals" />
      </Section>

      <Section className="bg-primary text-primary-foreground">
        <div className="grid grid-cols-1 gap-12 md:grid-cols-12">
          <div className="md:col-span-6">
            <Kicker className="text-primary-foreground/60">{t("home.orgKicker")}</Kicker>
            <h2 className="mt-6 font-display text-3xl font-light sm:text-4xl">{t("home.orgTitle")}</h2>
            <p className="mt-6 text-sm leading-relaxed text-primary-foreground/80 sm:text-base">{t("home.orgBody")}</p>
          </div>
          <div className="md:col-span-6">
            <ul data-testid="org-items" className="divide-y divide-primary-foreground/15 border-y border-primary-foreground/15">
              {t("home.orgItems").map((item) => (
                <li key={item} className="py-4 text-sm text-primary-foreground/85">
                  {item}
                </li>
              ))}
            </ul>
            <div className="mt-8 h-40 overflow-hidden border border-primary-foreground/15">
              <img src={GROWTH_IMAGE} alt="" className="h-full w-full object-cover" loading="lazy" />
            </div>
          </div>
        </div>
      </Section>

      <Section>
        <div className="border border-border bg-surface p-10 md:p-16">
          <h2 className="max-w-2xl font-display text-3xl font-light sm:text-4xl">{t("home.closingTitle")}</h2>
          <p className="mt-6 max-w-xl text-sm text-muted-foreground sm:text-base">{t("home.closingBody")}</p>
          <div className="mt-10 flex flex-col gap-4 sm:flex-row">
            <Button asChild size="lg" data-testid="closing-apply-cta" className="rounded-full bg-accent px-8 text-accent-foreground hover:bg-accent/90">
              <Link to={p(routeSlug("apply", lang))}>{t("cta.apply")}</Link>
            </Button>
            <Button asChild size="lg" variant="outline" data-testid="closing-careers-cta" className="rounded-full px-8">
              <Link to={p(routeSlug("careers", lang))}>{t("nav.careers")}</Link>
            </Button>
          </div>
        </div>
      </Section>
    </div>
  );
}
